import React, { FC, useContext } from 'react';

import {
  BeaconEventMarketingPageElementLocation,
  BeaconEventMarketingPageElementType,
  getI18nData,
  sendBeaconEvent,
  AvailableLanguages,
  useProductUrls
} from '@lucid/utils';
import { mqMax, mqMin, LucidTheme } from '@lucid/theme';

import Box from '../../components/box/box';
import Container from '../../components/container/container';
import LayoutContext from '../../components/context/layout-context';
import LanguageSwitcher from '../../components/language-switcher/language-switcher';
import Link from '../../components/link/link';
import { I18nDataEdges, ExtendedDrupalNode, LinkInterface } from '../../types/types';
import { ComponentLink } from '../../types/generics';

export interface SiteFooterApplicationLinks {
  url: string;
  image: string;
  alt: string;
  detail: string;
}

export interface SiteFooterSocialLink {
  name: string;
  logo: string;
  url: string;
}

export interface SiteFooterSocialLinks {
  node: SiteFooterSocialLink;
}

export interface SiteFooterOptionalProps {
  /** Adds the Apple & Google play store links to the footer. */
  applicationLinks?: SiteFooterApplicationLinks[];
  /** Append additional links to the footer bottom section. */
  bottomLinks?: ComponentLink[];
  /** Allows for a customPrefix to overwrite gatsby withPrefix function. */
  customPrefix?: string;
  /** The Language Switcher Disabled Languages. */
  disabledLanguages?: AvailableLanguages[];
  entityTranslations?: ExtendedDrupalNode[];
  /** Checks if the navbar is a visio page. */
  isVisio?: boolean;
  /** Checks if the navbar should show security. */
  showSecurity?: boolean;
  /** The social links used in the Footer component. */
  socialLinks?: SiteFooterSocialLinks[];
}

export interface SiteFooterProps extends SiteFooterOptionalProps {
  /** The i18n translation data. */
  i18nData: I18nDataEdges;
  /** The menu list for the Footer component. */
  menu: LinkInterface[];
}

const Footer: FC<SiteFooterProps> = ({
  applicationLinks = [],
  bottomLinks = [],
  customPrefix,
  disabledLanguages = ['da', 'sv'],
  entityTranslations,
  i18nData,
  isVisio = false,
  menu,
  socialLinks
}) => {
  const { language } = useContext(LayoutContext);
  const { shortLangPrefix, i18nKey, urlLangPrefix } = language;
  const i18n = getI18nData(i18nData, i18nKey);
  const { productUrl } = useProductUrls(urlLangPrefix);
  const visioQueryParams = '?type=visio';
  const baseLinks: ComponentLink[] = [
    {
      linkUrl: 'https://lucid.co/privacy',
      linkText: i18n('mktg-footer-about-privacy')
    },
    {
      linkUrl: 'https://lucid.co/legal',
      linkText: i18n('mktg-footer-legal')
    }
  ];

  const footerBottomLinks: ComponentLink[] = [...baseLinks, ...bottomLinks];

  return (
    <footer
      css={(theme: LucidTheme) => ({
        paddingTop: theme.space[3],
        background: theme.colors.coolGrey[0]
      })}
    >
      <Container displayFlex className="footerTop">
        <Box
          className="footerTop-links"
          css={(theme: LucidTheme) => ({
            display: 'grid',
            flexWrap: 'wrap',
            flex: '0 1 100%',
            gridTemplateColumns: `repeat(${menu.length <= 5 ? menu.length : 5}, 1fr)`,
            padding: `${theme.space[4]}px 0`,
            [mqMax.tabletSmall]: {
              gridTemplateColumns: 'repeat(3, 1fr)'
            },
            [mqMax.mobile]: {
              gridTemplateColumns: 'repeat(2, 1fr)'
            }
          })}
        >
          {menu.map(item => {
            return (
              <ul
                key={item.title}
                className="footerTop-linkGroup"
                css={{
                  flex: '0 1 20%',
                  listStyle: 'none',
                  margin: '0 0 2rem 0',
                  [mqMax.tablet]: {
                    flex: '0 1 31%',
                    marginBottom: '2.5rem'
                  },
                  [mqMax.mobile]: {
                    flex: '0 1 49%'
                  }
                }}
              >
                <li
                  className="footerTop-linkGroupHeading"
                  css={(theme: LucidTheme) => ({
                    fontWeight: theme.fontWeights.stronger,
                    fontSize: theme.fontSizes.fs16,
                    marginBottom: theme.space[2]
                  })}
                >
                  {item.title}
                </li>
                {item?.links?.map(subitem => {
                  let href = isVisio && subitem.fieldKey === 'pricing'
                    ? `${subitem?.link?.url!.path}${visioQueryParams}`
                    : subitem?.link?.url!.path;

                  if (productUrl && subitem.fieldProductLink) {
                    href = `${productUrl}${shortLangPrefix !== 'en' ? `/${shortLangPrefix}` : ''}${href}`;
                  }

                  if (subitem.fieldIsExternalSourceLink) {
                    href = subitem.fieldIsExternalSourceLink;
                  }

                  if (subitem.isEnabled === false) {
                    // use '=== false' instead of '!link.enabled'
                    // since this is used across all sites, err on the side of rendering the item
                    // in case "enabled" is removed from the query
                    return null;
                  }

                  if (subitem.fieldLinkOverride) {
                    href = subitem.fieldLinkOverride;
                  }

                  const beaconEvent = subitem.fieldBeaconElementDetail
                    ? {
                        name: 'clickedMarketingPageElement',
                        elementLocation: BeaconEventMarketingPageElementLocation.footer,
                        elementDetail: subitem.fieldBeaconElementDetail,
                        elementType: BeaconEventMarketingPageElementType.link
                      }
                    : null;
                  const linkTitle = subitem.fieldIsExternalSourceLinkTi ? subitem.fieldIsExternalSourceLinkTi : subitem.title;

                  return (
                    <li
                      key={linkTitle}
                      className="footerTop-linkGroupItem"
                      css={(theme: LucidTheme) => ({
                        margin: `${theme.space[3]}px 0`,
                        paddingRight: '1.5rem'
                      })}
                    >
                      <Link
                        beaconEvent={beaconEvent}
                        className="footerTop-linkGroupLink"
                        css={(theme: LucidTheme) => ({
                          fontWeight: theme.fontWeights.regular
                        })}
                        noPrefix={!!customPrefix}
                        onClick={() => subitem.fieldBeaconElementDetail}
                        to={customPrefix && href?.startsWith('/') ? `${customPrefix}${href}` : href}
                        underline="hover"
                        variant="subNavLink"
                      >
                        {linkTitle}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </Box>
      </Container>
      <Box
        className="footerBottom"
        css={(theme: LucidTheme) => ({
          padding: '1.5rem 0',
          background: theme.colors.coolGrey[5]
        })}
      >
        <Container displayFlex css={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Box
            className="footerBottom-left"
            css={{
              flex: '0 1 40%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'baseline',
              [mqMax.tabletSmall]: {
                flex: '1 1 100%',
                maxWidth: '100%'
              }
            }}
          >
            <LanguageSwitcher
              disabledLanguages={disabledLanguages}
              entityTranslations={entityTranslations!}
              shortLangPrefix={shortLangPrefix}
            />
            {footerBottomLinks.map(link => (
              <Link
                key={link.linkText}
                css={(theme: LucidTheme) => ({
                  marginRight: '1rem',
                  fontWeight: theme.fontWeights.regular,
                  '&:last-of-type': {
                    marginRight: '0'
                  }
                })}
                rel="noreferrer"
                target="_blank"
                to={link.linkUrl}
                variant="subNavLink"
              >
                {link.linkText}
              </Link>
            ))}
          </Box>
          <Box
            className="footerBottom-right"
            css={{
              flex: '0 1 60%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              [mqMin.default]: {
                flex: '1 1 100%',
                marginTop: '2rem',
                flexWrap: 'wrap'
              },
              [mqMin.tabletSmall]: {
                marginTop: 0,
                flexWrap: 'nowrap'
              },
              [mqMin.tablet]: {
                flex: 'unset'
              },
              [mqMin.tabletLandscape]: {
                flex: '1 1 60%'
              }
            }}
          >
            <ul
              className="footerBottom-socialLinks"
              css={(theme: LucidTheme) => [
                {
                  display: 'flex',
                  flex: '0 1 215px',
                  margin: 0,
                  listStyle: 'none',
                  color: theme.colors.primary,
                  justifyContent: 'space-between',
                  [mqMax.tabletSmall]: {
                    flex: '1 1 290px'
                  }
                },
                applicationLinks.length > 0 && {
                  margin: '0 2.5rem 0 0',
                  flex: '0 1 515px',
                }
              ]}
            >
              {socialLinks?.map(socialIcon => {
                const icon = socialIcon.node;
                return (
                  <li key={icon.name}>
                    <a
                      href={icon.url!}
                      onClick={
                        () =>
                          sendBeaconEvent({
                            name: 'clickedMarketingPageElement',
                            elementLocation:
                              BeaconEventMarketingPageElementLocation.footer,
                            elementDetail: icon.name,
                            elementType: BeaconEventMarketingPageElementType.link
                          })
                      }
                      rel="noreferrer"
                      target="_blank"
                    >
                      <img
                        alt={icon.name!}
                        css={{ height: '20px' }}
                        loading="lazy"
                        src={icon.logo!}
                      />
                    </a>
                  </li>
                );
              })}
            </ul>
            {applicationLinks.length > 0 && (
              <Box
                className="footerBottom-appLinks"
                css={(theme: LucidTheme) => ({
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  paddingTop: theme.space.s32,
                  [mqMin.mobile]: {
                    justifyContent: 'flex-end'
                  },
                  [mqMin.tabletSmall]: {
                    paddingTop: 0
                  }
                })}
              >
                {applicationLinks.map(link => (
                  <a
                    key={link.detail}
                    css={(theme: LucidTheme) => ({
                      display: 'inline-block',
                      marginRight: '1rem',
                      '&:first-of-type': {
                        marginRight: theme.space.s16
                      },
                      '&:last-of-type': {
                        marginRight: 0
                      },
                      [mqMax.tabletLandscape]: {
                        display: 'block',
                        marginRight: 0,
                        textAlign: 'right'
                      }
                    })}
                    href={link.url}
                    onClick={() =>
                      sendBeaconEvent({
                        name: 'clickedMarketingPageElement',
                        elementLocation: BeaconEventMarketingPageElementLocation.footer,
                        elementDetail: link.detail,
                        elementType: BeaconEventMarketingPageElementType.link
                      })}
                  >
                    <img
                      alt={link.alt}
                      css={{ height: '40px' }}
                      loading="lazy"
                      src={link.image}
                    />
                  </a>
                ))}
              </Box>
            )}
          </Box>
          <Box
            css={{
              marginTop: '0.25rem',
              fontSize: '0.75rem',
              flex: '1 1 100%',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <span aria-label="Copyright" role="img">
              &copy;
            </span>
            {` ${new Date().getFullYear()} Lucid Software Inc.`}
          </Box>
        </Container>
      </Box>
    </footer>
  );
};

export { Footer };
export default Footer;
